<template>
  <main class="backup-private-page">
    <AppNavBar :title="t('wallet.exportPrivateKey')" />

    <section class="warning-card">
      <div class="warning-card__title">
        <app-image src="@/assets/images/warn_icon.png" alt="" />
        <div data-strong>{{ t('wallet.specialReminder') }}</div>
      </div>
      <p>{{ t('wallet.privateCarefulExport') }}</p>
    </section>

    <section class="qrcode-card">
      <button v-if="!showQrCode" type="button" class="qrcode-mask" @click="showQrCode = true">
        <app-image src="@/assets/images/eye_close_icon.png" alt="" />
        <span>{{ t('wallet.clickShowPrivateKeyQr') }}</span>
      </button>
      <app-image v-else-if="qrUrl" :src="qrUrl" alt="" class="qrcode-image" />
    </section>

    <h2 class="section-title">{{ t('wallet.plaintextPrivateKey') }}</h2>

    <section class="private-key-card">
      <p>{{ privateKey }}</p>
    </section>

    <van-button block round type="primary" class="copy-btn" @click="copyPrivateKey">
      {{ t('wallet.copyPrivateKey') }}
    </van-button>

    <van-popup v-model:show="showCopyDialog" round class="copy-dialog">
      <section class="copy-dialog__card">
        <h3>{{ t('wallet.safetyReminder') }}</h3>
        <p class="copy-dialog__desc">{{ t('wallet.copyPrivateKeyAfter') }}</p>

        <button type="button" class="copy-dialog__check" @click="toggleCheck('check1')">
          <van-checkbox :model-value="check1" icon-size="18px" />
          <p>
            {{ t('wallet.copyMnemonicAfter1') }}
            <span>{{ t('wallet.copyMnemonicAfter2') }}</span>
            {{ t('wallet.copyMnemonicAfter3') }}
          </p>
        </button>

        <button type="button" class="copy-dialog__check" @click="toggleCheck('check2')">
          <van-checkbox :model-value="check2" icon-size="18px" />
          <p>
            {{ t('wallet.notPasteMnemonic1') }}
            <span>{{ t('wallet.notPasteMnemonic2') }}</span>
            {{ t('wallet.notPasteMnemonic3') }}
            <span>{{ t('wallet.notPasteMnemonic4') }}</span>
          </p>
        </button>

        <button type="button" class="copy-dialog__check" @click="toggleCheck('check3')">
          <van-checkbox :model-value="check3" icon-size="18px" />
          <p>
            {{ t('wallet.notShareMnemonic1') }}
            <span>{{ t('wallet.notShareMnemonic2') }}</span>
          </p>
        </button>

        <van-button
          block
          round
          type="primary"
          class="copy-dialog__submit"
          :disabled="!canSubmitCopy"
          @click="confirmCopy"
        >
          {{ t('common.confirm') }}
        </van-button>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import QRCode from 'qrcode';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { copyToClipboard } from '@/utils/clipboard';
import {
  clearSensitiveExportPayload,
  getSensitiveExportPayload
} from '@/utils/walletSession';

const { t } = useI18n();
const router = useRouter();
const showQrCode = ref(false);
const qrUrl = ref('');
const exportPayload = ref(getSensitiveExportPayload());
const showCopyDialog = ref(false);
const check1 = ref(false);
const check2 = ref(false);
const check3 = ref(false);

const privateKey = computed(() => String(exportPayload.value?.privateKey || '').trim());
const canSubmitCopy = computed(() => check1.value && check2.value && check3.value);

if (!privateKey.value) {
  router.replace('/wallet/backup');
}

function toggleCheck(key) {
  if (key === 'check1') check1.value = !check1.value;
  if (key === 'check2') check2.value = !check2.value;
  if (key === 'check3') check3.value = !check3.value;
}

function resetChecks() {
  check1.value = false;
  check2.value = false;
  check3.value = false;
}

function copyPrivateKey() {
  if (!privateKey.value) {
    showFailToast(t('wallet.privateKeyUnavailable'));
    return;
  }
  resetChecks();
  showCopyDialog.value = true;
}

async function confirmCopy() {
  if (!canSubmitCopy.value) {
    return;
  }
  const copied = await copyToClipboard(privateKey.value);
  if (copied) {
    showCopyDialog.value = false;
    showPassedToast(t('wallet.copySuccess'));
    return;
  }
  showFailToast(t('wallet.copyFailed'));
}

watch(privateKey, async (value) => {
  if (!value) {
    qrUrl.value = '';
    return;
  }
  qrUrl.value = await QRCode.toDataURL(value, {
    margin: 1,
    width: 220
  });
}, { immediate: true });

onBeforeUnmount(() => {
  clearSensitiveExportPayload();
});
</script>

<style scoped lang="scss">
.backup-private-page {
  min-height: var(--app-viewport-height);
  padding: 14px 14px 34px;
  background: #f7f8fa;
}

.warning-card {
  padding: 15px;
  border-radius: 10px;
  background: #f5dfe5;

  p {
    margin: 8px 0 0 30px;
    color: #e90026;
    font-size: 12px;
    line-height: 18px;
  }
}

.warning-card__title {
  display: flex;
  align-items: center;

  .app-image {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }

  [data-strong] {
    color: #e90026;
    font-size: 16px;
    font-weight: 500;
  }
}

.qrcode-card {
  display: flex;
  width: 200px;
  height: 200px;
  align-items: center;
  justify-content: center;
  margin: 40px auto 30px;
  border-radius: 10px;
  background: #d7e2ff;
}

.qrcode-mask {
  display: flex;
  width: 180px;
  height: 180px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: #eaeaea;

  .app-image {
    width: 20px;
    height: 20px;
  }

  span {
    margin-top: 10px;
    color: #666666;
    font-size: 12px;
    line-height: 18px;
  }
}

.qrcode-image {
  width: 170px;
  height: 170px;
  border-radius: 10px;
}

.section-title {
  margin: 0 0 30px;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
}

.private-key-card {
  padding: 16px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(17, 26, 52, 0.06);

  p {
    margin: 0;
    color: #111111;
    font-size: 14px;
    line-height: 22px;
    word-break: break-all;
  }
}

.copy-btn {
  margin-top: 30px;
}

.copy-dialog {
  width: calc(100vw - 56px);
  max-width: 360px;
  overflow: hidden;
}

.copy-dialog__card {
  padding: 18px 16px 16px;
  background: #ffffff;

  h3 {
    margin: 0;
    color: #111111;
    font-size: 17px;
    font-weight: 700;
    text-align: center;
  }
}

.copy-dialog__desc {
  margin: 18px 0 16px;
  color: #666666;
  font-size: 13px;
  line-height: 20px;
}

.copy-dialog__check {
  display: flex;
  width: 100%;
  align-items: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;

  & + & {
    margin-top: 12px;
  }

  :deep(.van-checkbox) {
    margin-right: 10px;
    flex-shrink: 0;
  }

  :deep(.van-checkbox__icon .van-icon) {
    border-color: #c8d0de;
  }

  :deep(.van-checkbox__icon--checked .van-icon) {
    border-color: #2667ff;
    background: #2667ff;
  }

  p {
    margin: 0;
    color: #333333;
    font-size: 13px;
    line-height: 20px;
  }

  span {
    color: #ff3b30;
  }
}

.copy-dialog__submit {
  margin-top: 22px;
}
</style>
